import React, { useCallback, useRef } from 'react';
import type { Language } from '../../types';
import { getTranslation } from '../../utils/translations';
import { formatDigitalRemainingLine, formatDigitalCountdown, formatClockTime } from '../../utils/intermittentFasting';

interface FastingSmartRingProps {
    mode: 'setup' | 'active' | 'scheduled';
    goalHours: number;
    remainingMs?: number;
    endTimestamp?: number | null;
    language?: Language;
    onGoalChange?: (hours: number) => void;
    onCenterClick: () => void;
    minGoal?: number;
    maxGoal?: number;
}

const SIZE = 260;
const CENTER = SIZE / 2;
const RADIUS = 110;
const KNOB_RADIUS = 12;

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function valueToAngle(value: number, min: number, max: number): number {
    const pct = (value - min) / (max - min);
    return -Math.PI / 2 + pct * 2 * Math.PI;
}

function angleToValue(angle: number, min: number, max: number): number {
    let normalized = angle + Math.PI / 2;
    while (normalized < 0) normalized += 2 * Math.PI;
    while (normalized >= 2 * Math.PI) normalized -= 2 * Math.PI;
    const pct = normalized / (2 * Math.PI);
    return clamp(Math.round(min + pct * (max - min)), min, max);
}

function pointOnCircle(angle: number): { x: number; y: number } {
    return {
        x: CENTER + RADIUS * Math.cos(angle),
        y: CENTER + RADIUS * Math.sin(angle),
    };
}

function describeArc(startAngle: number, endAngle: number): string {
    const start = pointOnCircle(startAngle);
    const end = pointOnCircle(endAngle);
    let sweep = endAngle - startAngle;
    if (sweep < 0) sweep += 2 * Math.PI;
    const largeArc = sweep > Math.PI ? 1 : 0;
    return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

const FastingSmartRing: React.FC<FastingSmartRingProps> = ({
    mode,
    goalHours,
    remainingMs = 0,
    endTimestamp,
    language,
    onGoalChange,
    onCenterClick,
    minGoal = 1,
    maxGoal = 48,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const svgRef = useRef<SVGSVGElement>(null);
    const draggingRef = useRef(false);

    // Setup mode calculations
    const angle = valueToAngle(goalHours, minGoal, maxGoal);
    const knob = pointOnCircle(angle);
    const startAngle = -Math.PI / 2;
    const setupPath = describeArc(startAngle, angle);

    // Active mode calculations
    const circumference = 2 * Math.PI * RADIUS;
    const totalGoalMs = goalHours * 60 * 60 * 1000;
    // Calculate progress: 0 to 1
    const rawProgress = 1 - (remainingMs / totalGoalMs);
    const progress = clamp(rawProgress, 0, 1);
    const strokeDashoffset = circumference - progress * circumference;

    const updateFromPointer = useCallback((clientX: number, clientY: number) => {
        if (mode !== 'setup' || !onGoalChange) return;
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const x = ((clientX - rect.left) / rect.width) * SIZE - CENTER;
        const y = ((clientY - rect.top) / rect.height) * SIZE - CENTER;
        const next = angleToValue(Math.atan2(y, x), minGoal, maxGoal);
        onGoalChange(next);
    }, [mode, onGoalChange, minGoal, maxGoal]);

    const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        if (mode !== 'setup') return;
        // Don't intercept clicks in the center circle
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
            const x = ((event.clientX - rect.left) / rect.width) * SIZE - CENTER;
            const y = ((event.clientY - rect.top) / rect.height) * SIZE - CENTER;
            const dist = Math.sqrt(x*x + y*y);
            if (dist < RADIUS - 20) return; // Inside center
        }

        draggingRef.current = true;
        svgRef.current?.setPointerCapture(event.pointerId);
        updateFromPointer(event.clientX, event.clientY);
    };

    const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        if (!draggingRef.current || mode !== 'setup') return;
        updateFromPointer(event.clientX, event.clientY);
    };

    const handlePointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
        if (mode !== 'setup') return;
        draggingRef.current = false;
        svgRef.current?.releasePointerCapture(event.pointerId);
    };

    return (
        <div className="relative flex flex-col items-center justify-center w-[260px] h-[260px] mx-auto select-none touch-none">
            {/* The SVG Ring */}
            <svg
                ref={svgRef}
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                className="absolute inset-0 w-full h-full"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                style={{ cursor: mode === 'setup' ? 'grab' : 'default' }}
            >
                {/* Background Track */}
                <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={RADIUS}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={14}
                    className="text-slate-100 dark:text-slate-800"
                />

                {mode === 'setup' ? (
                    <>
                        <path
                            d={setupPath}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={14}
                            strokeLinecap="round"
                            className="text-teal-500 transition-colors"
                        />
                        <circle
                            cx={knob.x}
                            cy={knob.y}
                            r={KNOB_RADIUS}
                            className="fill-white dark:fill-slate-900 stroke-teal-500 cursor-grabbing transition-colors"
                            strokeWidth={3}
                        />
                    </>
                ) : mode === 'scheduled' ? (
                    <path
                        d={setupPath}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={14}
                        strokeLinecap="round"
                        className="text-indigo-400 opacity-50 transition-all duration-1000 ease-linear"
                        strokeDasharray="8 8"
                    />
                ) : (
                    <>
                        <circle
                            cx={CENTER}
                            cy={CENTER}
                            r={RADIUS}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={14}
                            strokeLinecap="round"
                            className="text-emerald-500 transition-all duration-1000 ease-linear"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            transform={`rotate(-90 ${CENTER} ${CENTER})`}
                        />
                    </>
                )}
            </svg>

            {/* The Interactive Center */}
            <button
                onClick={onCenterClick}
                className={`absolute w-[180px] h-[180px] rounded-full flex flex-col items-center justify-center bg-white dark:bg-slate-900 shadow-[inset_0_4px_12px_rgba(0,0,0,0.05),0_8px_20px_rgba(0,0,0,0.04)] dark:shadow-[inset_0_4px_12px_rgba(255,255,255,0.02),0_8px_20px_rgba(0,0,0,0.3)] transition-all duration-500 active:scale-[0.98] border border-slate-50 dark:border-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
                    mode === 'setup' ? 'focus:ring-teal-500 drop-shadow-[0_0_15px_rgba(20,184,166,0.2)]' :
                    mode === 'active' ? 'focus:ring-emerald-500 drop-shadow-[0_0_15px_rgba(16,185,129,0.2)]' :
                    'focus:ring-indigo-400 drop-shadow-[0_0_15px_rgba(129,140,248,0.2)]'
                }`}
                aria-label={mode === 'setup' ? t('fastingStartNow') : t('fastingTimer')}
            >
                {mode === 'setup' ? (
                    <>
                        <span className="text-6xl font-black text-slate-800 dark:text-slate-100 tabular-nums leading-none mb-1">
                            {goalHours}
                        </span>
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                            {t('fastingHoursGoal')?.replace('{hours}', '').trim()}
                        </span>
                        <div className="absolute bottom-6 text-[10px] font-bold text-teal-500 flex items-center gap-1 opacity-80 uppercase tracking-widest">
                            {t('fastingStartNow')}
                        </div>
                    </>
                ) : mode === 'scheduled' ? (
                    <>
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">
                            {t('fastingStartsIn')?.replace('{time}', '').trim() || 'Starts in'}
                        </span>
                        <span className="text-4xl font-black text-indigo-400 tabular-nums leading-none mb-2 tracking-tight">
                            {formatDigitalCountdown(remainingMs)}
                        </span>
                        <div className="absolute bottom-6 text-[10px] font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1 opacity-80 uppercase tracking-widest">
                            {t('fastingTargetHours')?.replace('{hours}', goalHours.toString()) || `Target: ${goalHours} hours`}
                        </div>
                    </>
                ) : (
                    <>
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">
                            {t('remaining') || 'Remaining'}
                        </span>
                        <span className="text-4xl font-black text-emerald-500 tabular-nums leading-none mb-2 tracking-tight">
                            {formatDigitalCountdown(remainingMs)}
                        </span>
                        {endTimestamp && (
                            <span className="absolute bottom-6 text-[10px] font-bold text-slate-400 dark:text-slate-500 px-2 text-center leading-tight">
                                {t('fastingEndsAt')?.replace('{time}', formatClockTime(endTimestamp, language))}
                            </span>
                        )}
                    </>
                )}
            </button>
        </div>
    );
};

export default FastingSmartRing;
